import {
  Box,
  Breadcrumbs,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  Divider,
  FormControl,
  FormLabel,
  Grid2,
  Link,
  MenuItem,
  Paper,
  Select,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import { BarChart, PieChart } from "@mui/x-charts";
import React, { useEffect, useState } from "react";
import { NavLink, useNavigate } from "react-router-dom";
import { getDBStats, LiteratureDBStats } from "../../api/literatureDatabase";
import { useAccountManager } from "../../hooks/useAccountHook";
import { usePublicManager } from "../../hooks/usePublicHook";

interface DropdownOption {
  value: number;
  label: string;
}

export default function Dashboard() {
  const navigate = useNavigate();

  const [isLoggedIn, setIsLoggedIn] = useState(false);
  const [showProfilePopup, setShowProfilePopup] = useState(false);
  const [email, setEmail] = useState("");
  const [firstName, setFirstName] = useState("");
  const [lastName, setLastName] = useState("");
  const [institution, setInstitution] = useState("");
  const [sectorOptions, setSectorOptions] = useState<DropdownOption[]>([]);
  const [countryOptions, setCountryOptions] = useState<DropdownOption[]>([]);
  const [roleOptions, setRoleOptions] = useState<DropdownOption[]>([]);
  const [selectedRole, setSelectedRole] = useState("");
  const [selectedSector, setSelectedSector] = useState("");
  const [selectedCountry, setSelectedCountry] = useState("");
  const [errorMessage, setErrorMessage] = useState("");

  const [buttonText, setButtonText] = useState("Save and Continue");

  const { useFetchCountries, useFetchRoles, useFetchSectors } =
    usePublicManager();
  const { data: countriesData, error: countriesError } = useFetchCountries();
  const { data: rolesData, error: rolesError } = useFetchRoles();
  const { data: sectorsData, error: sectorsError } = useFetchSectors();

  const { useUpdateProfile, useFetchProfileByID } = useAccountManager();
  const { mutate: updateProfile } = useUpdateProfile();
  const { data: profileData, error: profileError } = useFetchProfileByID(
    Number(localStorage.getItem("id")),
  );

  const [stats, setStats] = useState<LiteratureDBStats>();
  useEffect(() => {
    // Fetching and processing DB stats
    (async () => {
      const MAX_STAT_ENTRIES = 15;

      const data = (await getDBStats()).data.data as LiteratureDBStats;
      // Processing element distribution data
      data.elemdist = Object.entries(data.elemdist)
        // Sorting distribution data in descending order
        .sort(([xk, xv], [yk, yv]) => yv - xv)
        // Clumping together entries more than MAX_STAT_ENTRIES into "Others"
        .reduce(
          (acc, [k, v], index) =>
            index < MAX_STAT_ENTRIES
              ? { ...acc, [k]: v }
              : { ...acc, Others: acc.Others ? acc.Others + v : v },
          {} as Record<string, number>,
        );
      // Same as above but for phases
      data.phasedist = Object.entries(data.phasedist)
        .sort(([xk, xv], [yk, yv]) => yv - xv)
        .reduce(
          (acc, [k, v], index) =>
            index < MAX_STAT_ENTRIES
              ? { ...acc, [k]: v }
              : { ...acc, Others: acc.Others ? acc.Others + v : v },
          {} as Record<string, number>,
        );
      setStats(data);
    })();
  }, []);

  useEffect(() => {
    setEmail(localStorage.getItem("email") || "");
    if (localStorage.getItem("is_user_logged_in") === "true") {
      setIsLoggedIn(true);
    }

    if (profileError === null && profileData) {
      console.log(profileData);
      if (profileData.data.profileStatus == 2) {
        setShowProfilePopup(false);
      } else {
        setFirstName(profileData.data.firstName);
        setLastName(profileData.data.lastName);
        setInstitution(profileData.data.institution);
        setEmail(profileData.data.email);
        setSelectedRole(String(profileData.data.roleID || ""));
        setSelectedSector(String(profileData.data.sectorID || ""));
        setSelectedCountry(String(profileData.data.countryID || ""));
        setShowProfilePopup(true);
      }
    }
  }, [profileData]);

  const validateInputs = (): boolean => {
    if (!firstName.trim() || !lastName.trim()) {
      setErrorMessage("First Name and Last Name are required.");
      return false;
    }

    if (!email || !/\S+@\S+\.\S+/.test(email)) {
      setErrorMessage("Please enter a valid email address.");
      return false;
    }

    if (!institution.trim()) {
      setErrorMessage("Institution is required.");
      return false;
    }

    if (!selectedCountry || !selectedRole || !selectedSector) {
      setErrorMessage("Please select Country, Role, and Sector.");
      return false;
    }

    setErrorMessage("");
    return true;
  };

  const handleSubmit = (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();

    setButtonText("Saving...");

    if (!validateInputs()) {
      return;
    }

    const userID = Number(localStorage.getItem("id") as string);

    const payload = {
      firstName,
      lastName,
      email,
      ID: userID,
      institution,
      roleID: Number(selectedRole),
      countryID: Number(selectedCountry),
      sectorID: Number(selectedSector),
      profileStatus: 2,
    };

    updateProfile(payload, {
      onSuccess: () => {
        setShowProfilePopup(false);
        setButtonText("Save and Continue");
      },
      onError: () => {
        setErrorMessage("We could not create your account. Please try again.");
        setButtonText("Save and Continue");
      },
    });
  };

  useEffect(() => {
    if (sectorsError === null && sectorsData) {
      const sectors = sectorsData.data.map((sector) => ({
        value: sector.id,
        label: sector.label,
      }));
      setSectorOptions(sectors);
    }
    if (rolesError === null && rolesData) {
      const roles = rolesData.data.map((role) => ({
        value: role.id,
        label: role.label,
      }));
      setRoleOptions(roles);
    }

    if (countriesError === null && countriesData) {
      const countries = countriesData.data.map((country) => ({
        value: country.id,
        label: country.label,
      }));
      setCountryOptions(countries);
    }
  }, [countriesData, rolesData, sectorsData]);

  return (
    <>
      <Box
        sx={{
          display: "flex",
          flexDirection: "column",
          minHeight: "100vh",
          backgroundColor: "#f9f9f9",
          p: 3,
        }}
      >
        {/* Breadcrumbs */}
        <Breadcrumbs aria-label="breadcrumb" sx={{ mb: 3 }}>
          <Link
            underline="hover"
            color="inherit"
            onClick={() => navigate("/")}
            sx={{ cursor: "pointer" }}
          >
            Home
          </Link>
          <Typography color="text.primary">Dashboard</Typography>
        </Breadcrumbs>

        <Stack spacing={2} className="w-full">
          <Paper elevation={3} className="w-full p-8 bg-orange-500 text-white">
            <Typography variant="h2" fontWeight={"bold"}>
              COD'HEM
            </Typography>
            <Typography gutterBottom variant="subtitle1">
              Consolidated Database of High Entropy Materials
            </Typography>
            <div className="flex gap-2 mt-6">
              {isLoggedIn ? (
                <></>
              ) : (
                <NavLink
                  to="/signin"
                  className="py-2 px-6 my-auto rounded-md bg-purple-700 text-white font-light transition-all hover:cursor-pointer hover:bg-purple-800"
                >
                  Login or Register
                </NavLink>
              )}

              <NavLink
                to="/database"
                className={
                  "py-2 px-6 my-auto rounded-md hover:cursor-pointer" +
                  " " +
                  (isLoggedIn
                    ? "bg-purple-700 text-white font-light transition-all hover:bg-purple-800"
                    : "border-[1px] border-purple-700 text-purple-700 font-light transition-all hover:bg-purple-700 hover:text-white")
                }
              >
                Explore Literature Database
              </NavLink>
            </div>
          </Paper>

          <Paper elevation={3} className="w-full p-8">
            {stats ? (
              <>
                <Typography variant="h5">Database Statistics</Typography>
                <Typography>
                  Visualizing data from the High Entropy Materials Database
                </Typography>
                <Grid2 container spacing={1} marginTop={2}>
                  <Grid2 size={2}>
                    <Stack spacing={1} height={"100%"}>
                      <Paper
                        variant="outlined"
                        sx={{
                          display: "grid",
                          flexGrow: 1,
                          padding: 2,
                        }}
                      >
                        <Typography gutterBottom variant="subtitle1">
                          Total compositions
                        </Typography>
                        <Typography variant="h3" align="center">
                          {Number(stats.compositions).toLocaleString()}
                        </Typography>
                      </Paper>
                      <Paper
                        variant="outlined"
                        sx={{
                          display: "grid",
                          flexGrow: 1,
                          padding: 2,
                        }}
                      >
                        <Typography gutterBottom variant="subtitle1">
                          Total DOIs
                        </Typography>
                        <Typography variant="h3" align="center">
                          {Number(stats.dois).toLocaleString()}
                        </Typography>
                      </Paper>
                    </Stack>
                  </Grid2>
                  <Grid2 size={5}>
                    <Paper
                      variant="outlined"
                      sx={{
                        display: "grid",
                        flexGrow: 1,
                        padding: 2,
                        height: "100%",
                      }}
                    >
                      <Typography gutterBottom variant="subtitle1">
                        Element distribution
                      </Typography>
                      <PieChart
                        series={[
                          {
                            data: Object.entries(stats.elemdist).map(
                              ([label, value], id) => ({
                                id,
                                label,
                                value,
                              }),
                            ),
                            innerRadius: "65%",
                          },
                        ]}
                        height={300}
                        slotProps={{
                          legend: {
                            direction: "column",
                            position: {
                              vertical: "middle",
                              horizontal: "right",
                            },
                            itemMarkWidth: 10,
                            itemMarkHeight: 10,
                            markGap: 5,
                            itemGap: 0,
                          },
                        }}
                      />
                    </Paper>
                  </Grid2>
                  <Grid2 size={5}>
                    <Paper
                      variant="outlined"
                      sx={{
                        display: "grid",
                        flexGrow: 1,
                        padding: 2,
                      }}
                    >
                      <Typography gutterBottom variant="subtitle1">
                        Phase distribution
                      </Typography>
                      <PieChart
                        series={[
                          {
                            data: Object.entries(stats.phasedist).map(
                              ([label, value], id) => ({
                                id,
                                label:
                                  // Truncating long labels to preventing hindering the plot
                                  label.length > 8
                                    ? `${label.slice(0, 8)}...`
                                    : label,
                                value,
                              }),
                            ),
                            innerRadius: "65%",
                          },
                        ]}
                        height={300}
                        slotProps={{
                          legend: {
                            direction: "column",
                            position: {
                              vertical: "middle",
                              horizontal: "right",
                            },
                            padding: 20,
                            itemMarkWidth: 10,
                            itemMarkHeight: 10,
                            markGap: 5,
                            itemGap: 0,
                          },
                        }}
                      />
                    </Paper>
                  </Grid2>
                  <Grid2 size={12}>
                    <Paper
                      variant="outlined"
                      sx={{
                        display: "grid",
                        flexGrow: 1,
                        padding: 2,
                      }}
                    >
                      <Typography gutterBottom variant="subtitle1">
                        Element distribution in distinctive compositions
                      </Typography>
                      {(() => {
                        const data = Object.entries(stats.elemdist).map(
                          ([label, value]) => ({
                            label:
                              // Truncating long labels to preventing hindering the plot
                              label.length > 8
                                ? `${label.slice(0, 8)}...`
                                : label,
                            value,
                          }),
                        );
                        return (
                          <BarChart
                            xAxis={[
                              {
                                scaleType: "band",
                                data: data.map((x) => x.label),
                              },
                            ]}
                            series={[
                              {
                                data: data.map((x) => x.value),
                              },
                            ]}
                            height={300}
                          />
                        );
                      })()}
                    </Paper>
                  </Grid2>
                </Grid2>
              </>
            ) : (
              "Data could not be loaded."
            )}
          </Paper>
        </Stack>
      </Box>

      {/* Profile Completion Popup */}
      <Dialog
        open={showProfilePopup}
        onClose={() => setShowProfilePopup(false)}
        className={"rounded-md shadow-md shadow-lg"}
      >
        <DialogContent>
          <Typography
            component="h1"
            variant="h6"
            sx={{
              display: "flex",
              justifyContent: "center",
              alignItems: "center",
              mb: 2,
            }}
            className="text-lg font-extrabold text-black"
          >
            Please Complete Your Profile
          </Typography>
          <Box
            component="form"
            onSubmit={handleSubmit}
            noValidate
            sx={{
              display: "flex",
              flexDirection: "column",
              gap: 2,
              width: "100%",
            }}
          >
            {/* First Name & Last Name */}
            <Stack direction="row" spacing={2}>
              <FormControl fullWidth>
                <FormLabel className="text-sm font-bold text-black">
                  First Name
                </FormLabel>
                <TextField
                  placeholder="First Name"
                  value={firstName}
                  onChange={(e) => setFirstName(e.target.value)}
                  required
                />
              </FormControl>
              <FormControl fullWidth>
                <FormLabel className="text-sm font-bold text-black">
                  Last Name
                </FormLabel>
                <TextField
                  placeholder="Last Name"
                  value={lastName}
                  onChange={(e) => setLastName(e.target.value)}
                  required
                />
              </FormControl>
            </Stack>

            {/* Email Field */}
            <FormControl fullWidth>
              <FormLabel className="text-sm font-bold text-black">
                Email
              </FormLabel>
              <TextField
                type="email"
                placeholder="your@email.com"
                value={email}
                required
                disabled={true}
                className="text-sm font-bold text-black"
              />
            </FormControl>

            {/* Institution & Country */}
            <Stack direction="row" spacing={2}>
              <FormControl fullWidth>
                <FormLabel className="text-sm font-bold text-black">
                  Institution
                </FormLabel>
                <TextField
                  placeholder="Your Institution"
                  value={institution}
                  onChange={(e) => setInstitution(e.target.value)}
                  required
                  className="text-sm font-bold text-black"
                />
              </FormControl>
              <FormControl fullWidth>
                <FormLabel className="text-sm font-bold text-black">
                  Country
                </FormLabel>
                <Select
                  value={selectedCountry}
                  onChange={(e) => setSelectedCountry(e.target.value)}
                  displayEmpty
                  required
                >
                  <MenuItem value="" disabled>
                    Select Country
                  </MenuItem>
                  {countryOptions.map((option) => (
                    <MenuItem key={option.value} value={option.value}>
                      {option.label}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Stack>

            {/* Sector & Role */}
            <Stack direction="row" spacing={2}>
              <FormControl fullWidth>
                <FormLabel className="text-sm font-bold text-black">
                  Sector
                </FormLabel>
                <Select
                  value={selectedSector}
                  onChange={(e) => setSelectedSector(e.target.value)}
                  displayEmpty
                  required
                >
                  <MenuItem value="" disabled>
                    Select Sector
                  </MenuItem>
                  {sectorOptions.map((option) => (
                    <MenuItem key={option.value} value={option.value}>
                      {option.label}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
              <FormControl fullWidth>
                <FormLabel>Role</FormLabel>
                <Select
                  value={selectedRole}
                  onChange={(e) => setSelectedRole(e.target.value)}
                  displayEmpty
                  required
                >
                  <MenuItem value="" disabled>
                    Select Role
                  </MenuItem>
                  {roleOptions.map((option) => (
                    <MenuItem key={option.value} value={option.value}>
                      {option.label}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Stack>

            {/* Error Message */}
            {errorMessage && (
              <Typography color="error" textAlign="center" className="text-xs">
                {errorMessage}
              </Typography>
            )}

            <Button
              variant="contained"
              type="submit"
              className={
                "border-[1px] text-white bg-orange-600 border-purple-700 font-bold transition-all hover:bg-purple-700 hover:text-white "
              }
            >
              {buttonText}
            </Button>
          </Box>
        </DialogContent>
        <DialogActions
          sx={{ flexDirection: "column", alignItems: "center", gap: 2 }}
        >
          <Divider sx={{ width: "100%" }} />
          <Button
            onClick={() => setShowProfilePopup(false)}
            sx={{
              textTransform: "none",
              color: "gray",
              fontSize: "0.875rem",
            }}
          >
            Remind Me Later
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
}
